/**
 * Generate Study Dialog - Step 5: Select Countries
 *
 * Migrated from: lego/page/etmf/overview/generate-study/Step5.jsx
 *
 * Multi-select table for selecting countries
 */

import React, { useEffect, useMemo, useState } from 'react'
import { FormikProps } from 'formik'
import { useDialogContext } from './context/useDialogContext'
import { useServiceContext } from '../../../../root'
import type { FormValues, SelectOption } from './context/DialogContext'
import Icon from '@mdsol/lego/lib/Icon'

interface Step5Props extends Partial<FormikProps<FormValues>> {}

export function Step5({ values, setFieldValue, errors }: Step5Props) {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const dialogData = useDialogContext()
  const [countries, setCountries] = useState<SelectOption[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>(values?.countries ?? [])
  const [selectAll, setSelectAll] = useState(false)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const sortedCountries = useMemo(() => {
    return [...countries].sort((a, b) => {
      const nameA = (a.label || '').toLowerCase()
      const nameB = (b.label || '').toLowerCase()
      if (nameA < nameB) return sortDir === 'asc' ? -1 : 1
      if (nameA > nameB) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }, [countries, sortDir])

  const handleSort = (e: React.MouseEvent) => {
    e.preventDefault()
    setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
  }

  useEffect(() => {
    loadCountries()
  }, [])

  const loadCountries = async () => {
    const result = await dialogData.loadCountryOptions({ sortDesc: true })
    setCountries(result.elements)

    if (!result.isCached) {
      setFieldValue?.('countries', [])
      setSelectedIds([])
    } else {
      setSelectedIds(values?.countries ?? [])
    }
  }

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    setSelectAll(checked)

    if (checked) {
      const allIds = countries.map((c) => c.value)
      setSelectedIds(allIds)
      setFieldValue?.('countries', allIds)
    } else {
      setSelectedIds([])
      setFieldValue?.('countries', [])
    }
  }

  const handleSelectRow = (countryId: number) => {
    const newSelection = selectedIds.includes(countryId)
      ? selectedIds.filter((id) => id !== countryId)
      : [...selectedIds, countryId]

    setSelectedIds(newSelection)
    setFieldValue?.('countries', newSelection)
    setSelectAll(newSelection.length === countries.length)
  }

  return (
    <div className="step step5">
      <div className="form-group w100p mb0">
        <div className="mcc-form-group">
          <label className="has-step-circle mb11px">
            <span className="step-circle">4</span>
            <span>{translate('ETMF_GENERATE_TRIAL_STRCT_TITLE_STEP5')}</span>
          </label>
        </div>
        {countries.length === 0 ? (
          <div className="no-results">{translate('GENERATE_STUDY_NO_RESULTS_FILTERED_COUNTRY_STEP')}</div>
        ) : (
          <div className="theme-sandman">
            <table className="chita-table fsiTable table">
              <thead>
                <tr>
                  <th className="clipped-cell with-checkbox">
                    <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                  </th>
                  <th>
                    <a href="#" className={`sort ed`} onClick={handleSort}>
                      {translate('NAME')}
                      <span> </span>
                      <Icon className="icon-xsmall" name={sortDir === 'asc' ? 'carat-up' : 'carat-down'} />
                    </a>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedCountries.map((country) => (
                  <tr key={country.value}>
                    <td className="clipped-cell with-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(country.value)}
                        onChange={() => handleSelectRow(country.value)}
                      />
                    </td>
                    <td className="word-break" title={country.label}>
                      {country.label}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {errors?.countries && <div className="error-message">{errors.countries}</div>}
      </div>
    </div>
  )
}

export default Step5
